import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import axios from '../config/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setGuest = useAuthStore((state) => state.setGuest);

  const handleGuest = () => {
    setGuest();
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        setAuth(response.data.user, response.data.token, response.data.refreshToken);
        navigate('/');
      } else {
        const errorMessages: Record<string, string> = {
          'INVALID_CREDENTIALS': 'Correo o contraseña incorrectos',
          'EMAIL_NOT_VERIFIED': 'Tu correo no ha sido verificado. Revisa tu bandeja de entrada.',
          'TOO_MANY_LOGIN_ATTEMPTS': 'Demasiados intentos. Intenta en 5 minutos.',
          'INTERNAL_ERROR': 'Error del servidor. Intenta más tarde.',
        };
        setError(errorMessages[response.data.error] || response.data.error || 'Error al iniciar sesión');
      }
    } catch (err: any) {
      const errorCode = err.response?.data?.error;
      const errorMessages: Record<string, string> = {
        'INVALID_CREDENTIALS': 'Correo o contraseña incorrectos',
        'EMAIL_NOT_VERIFIED': 'Tu correo no ha sido verificado. Revisa tu bandeja de entrada.',
        'TOO_MANY_LOGIN_ATTEMPTS': 'Demasiados intentos. Intenta en 5 minutos.',
        'INTERNAL_ERROR': 'Error del servidor. Intenta más tarde.',
      };
      setError(errorMessages[errorCode] || 'No se pudo conectar al servidor. Verifica tu conexión a internet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/logo.jpeg" alt="Fuigo" className="mx-auto h-24 w-auto mb-4" />
          <p className="text-lg text-blue-600 font-medium mb-4">
            compara, elige y llega mas rapido
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Inicia sesión en tu cuenta
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Correo electrónico"
              />
            </div>
            <div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Contraseña"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link to="/forgot-password" className="block text-sm text-blue-600 hover:text-blue-800">
              ¿Olvidaste tu contraseña?
            </Link>
            <Link to="/register" className="block text-sm text-blue-600 hover:text-blue-800">
              ¿No tienes cuenta? Regístrate
            </Link>
          </div>

          {/* Modo invitado — requerido por App Store Guideline 5.1.1(v) */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gray-50 px-2 text-gray-400">o</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGuest}
            className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Continuar sin cuenta
          </button>
        </form>

        {/* Privacy Policy & Footer */}
        <div className="text-center pt-2">
          <a
            href="https://www.fuigo.co/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 underline hover:text-blue-600"
          >
            Política de Privacidad
          </a>
        </div>

        {/* Footer social */}
        <div className="flex flex-col items-center gap-3 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/share/1JAibJERKi/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-sm font-medium">Síguenos en Facebook</span>
            </a>
            <a
              href="https://www.facebook.com/share/1JAibJERKi/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
            >
              👍 Me gusta
            </a>
          </div>
          <a
            href="mailto:info@fuigo.co"
            className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            info@fuigo.co
          </a>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Fuigo. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
}
